import { Box, Fade, Grid, Typography } from "@mui/material";
import { useContext, useEffect, useState, useRef } from "react";
import { DatePicker } from "rsuite";
import "rsuite/dist/rsuite.min.css";
import { PropertyContext } from "../../../../contexts/PropertyContext";
import useQuery from "../../../../shared/components/hooks/useQuery";
import CircularProgress from "../../../../shared/components/loader/circularProgress";
import InsightAccordion from "../insight-accordion";
import useMutation from "../../../../shared/components/hooks/useMutation";
import { getLocalStorageString } from "../../../../shared/utils/localStorage";
import { formatDate } from "../../../../shared/utils/formatter";

interface InsightData {
  id: string;
  title: string;
  text: string;
  summary: string;
  time_period: string;
  isThinking: boolean;
  isDone: boolean;
}

const DashboardInsights = () => {
  const { propertyCode, propertyId, pmsList } = useContext(PropertyContext);
  const { modifyData } = useMutation();

  const configQuery = useQuery(
    `settings/my?propertyId=${propertyId ?? ""}`,
    undefined,
    null,
    { enabled: !!propertyId },
  );

  const [insights, setInsights] = useState<InsightData[]>([]);
  const [loadingInsights, setLoadingInsights] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  const autoSetDateRef = useRef<string | null>(null);
  const activeEventSources = useRef<EventSource[]>([]);

  const fetchIdRef = useRef<number>(0);

  useEffect(() => {
    return () => {
      activeEventSources.current.forEach((es) => es.close());
    };
  }, []);

  useEffect(() => {
    const rawData = (configQuery as any).data?.data;

    if (configQuery.data && !rawData) {
      setLoadingInsights(false);
      setInsights([]);
      return;
    }

    if (!rawData || !propertyId || !propertyCode) return;

    const datePayload = selectedDate ? formatDate(selectedDate) : undefined;

    if (autoSetDateRef.current === datePayload && datePayload !== undefined) {
      autoSetDateRef.current = null;
      return;
    }

    activeEventSources.current.forEach((es) => es.close());
    activeEventSources.current = [];
    const currentFetchId = ++fetchIdRef.current;

    const updateInsight = (id: string, updates: Partial<InsightData>) => {
      if (fetchIdRef.current !== currentFetchId) return;

      setInsights((prev) =>
        prev.map((ins) => (ins.id === id ? { ...ins, ...updates } : ins))
      );
    };

    const fetchInsights = async () => {
      try {
        const configs = rawData.settings?.configurations || [];
        const userID = getLocalStorageString("userID");

        if (configs.length === 0) {
          setLoadingInsights(false);
          setInsights([]);
          return;
        }

        setInsights(
          configs.map((config: any) => ({
            id: String(config.id),
            title: config.name || "Insights",
            text: "Initializing...",
            summary: "",
            time_period: "N/A",
            isThinking: true,
            isDone: false,
          }))
        );

        setLoadingInsights(false);

        const requests = configs.map(async (config: any) => {
          const configId = String(config.id);

          const postResponse = await modifyData(
            `${process.env.REACT_APP_API_URL}/intelligence/dashboard-insights`,
            "post",
            {
              user_id: userID,
              property_id: propertyCode,
              pms_list: pmsList || [],
              configuration_id: config.id,
              ...(datePayload && { snapshot_date: datePayload }),
              configuration: [
                {
                  id: config.id,
                  name: config.name,
                  date: config.date,
                  settings: config.settings,
                },
              ],
            },
            false
          );

          if (postResponse?.snapshot_date && !autoSetDateRef.current) {
            autoSetDateRef.current = postResponse.snapshot_date;
            const [year, month, day] = postResponse.snapshot_date.split("-").map(Number);
            setSelectedDate(new Date(year, month - 1, day));
          }

          if (postResponse?.from_cache === true || postResponse?.insights) {
            updateInsight(configId, {
              text: postResponse.insights,
              summary: postResponse.summary,
              time_period: postResponse.time_period || "daily",
              isThinking: false,
              isDone: true,
            });
            return;
          }

          if (!postResponse?.job_id) {
            updateInsight(configId, { text: "Error: No job_id returned from API.", isThinking: false, isDone: true });
            return;
          }

          return new Promise((resolve) => {
            const sseUrl = `${process.env.REACT_APP_API_URL}/intelligence/dashboard-insights/stream/${postResponse.job_id}`;
            const es = new EventSource(sseUrl);
            activeEventSources.current.push(es);

            let accumulatedInsights = "";
            let hasStartedStreaming = false;
            let isResolved = false;

            const timeoutId = setTimeout(() => {
              if (!isResolved) {
                updateInsight(configId, { text: accumulatedInsights + "\n\n(Stream timed out)", isThinking: false, isDone: true });
                completeStream({});
              }
            }, 60000);

            const completeStream = (finalData: any) => {
              if (isResolved) return;
              isResolved = true;
              clearTimeout(timeoutId);

              es.close();
              activeEventSources.current = activeEventSources.current.filter((e) => e !== es);
              resolve(true);
            };

            es.addEventListener("close", () => {
              if (!isResolved) {
                updateInsight(configId, { isThinking: false, isDone: true });
                completeStream({});
              }
            });

            es.addEventListener("message", (event: any) => {
              try {
                const data = JSON.parse(event.data);

                if (data.status && data.status !== "done" && data.message) {
                  if (!hasStartedStreaming) updateInsight(configId, { text: data.message, isThinking: true });
                }

                if (data.token !== undefined) {
                  if (!hasStartedStreaming) {
                    hasStartedStreaming = true;
                    accumulatedInsights = "";
                  }

                  let tokenText = data.token;
                  try { tokenText = JSON.parse(tokenText); } catch (e) { }
                  accumulatedInsights += tokenText;

                  updateInsight(configId, { text: accumulatedInsights, isThinking: false });
                }

                const finalPayload = data.result || (data.insights !== undefined ? data : null);

                if (data.status === "done" || finalPayload) {
                  updateInsight(configId, {
                    text: finalPayload.insights || accumulatedInsights,
                    summary: finalPayload.summary,
                    time_period: finalPayload.time_period || "daily",
                    isThinking: false,
                    isDone: true
                  });

                  if (finalPayload.snapshot_date && !autoSetDateRef.current) {
                    autoSetDateRef.current = finalPayload.snapshot_date;
                    const [year, month, day] = finalPayload.snapshot_date.split("-").map(Number);
                    setSelectedDate(new Date(year, month - 1, day));
                  }

                  completeStream(data);
                }
              } catch (e) {
                if (typeof event.data === "string" && !event.data.includes("{")) {
                  if (!hasStartedStreaming) hasStartedStreaming = true;
                  accumulatedInsights += event.data;
                  updateInsight(configId, { text: accumulatedInsights, isThinking: false });
                }
              }
            });

            es.onerror = (err) => {
              if (isResolved) return;
              if (accumulatedInsights.length === 0) {
                updateInsight(configId, { text: "Error connecting to AI stream.", isThinking: false, isDone: true });
              }
              completeStream({});
            };
          });
        });

        await Promise.allSettled(requests);
      } catch (err) {
        if (fetchIdRef.current === currentFetchId) {
          setError("An unexpected error occurred");
        }
      }
    };

    fetchInsights();
  }, [configQuery.data, propertyId, propertyCode, pmsList, selectedDate]);

  const isLoading = configQuery.loading || loadingInsights;
  const isConfigMissing = configQuery.data && (configQuery as any).data?.data === null;

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
        <DatePicker
          oneTap
          placeholder="Select Date"
          placement="topEnd"
          style={{ width: 200 }}
          value={selectedDate}
          onChange={(val) => setSelectedDate(val)}
          format="yyyy-MM-dd"
        />
      </Box>

      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", width: "100%", minHeight: "250px", mt: -2.5 }}>
          <CircularProgress />
          <Typography ml={2} color="text.secondary" variant="body2">Loading configurations...</Typography>
        </Box>
      ) : error ? (
        <Box sx={{ textAlign: "center", p: 4, minHeight: "300px", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Typography color="error" variant="body2">{error}</Typography>
        </Box>
      ) : isConfigMissing ? (
        <Box sx={{ p: 4, textAlign: "center", minHeight: "200px", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
          <Typography color="text.secondary" variant="body2" mb={1}>
            No configuration found for this property.
          </Typography>
          <Typography color="text.secondary" variant="body2" mb={2}>
            Please go and set the configuration menu to generate insights.
          </Typography>
        </Box>
      ) : insights.length === 0 ? (
        <Box sx={{ border: "1px dashed #ccc", p: 4, borderRadius: "8px", textAlign: "center", minHeight: "200px", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Typography color="text.secondary" variant="body2">
            No actionable insights generated for this property.
          </Typography>
        </Box>
      ) : (
        <Fade in={true} timeout={500}>
          <Grid container spacing={2}>
            {insights.map((insight) => {
              const displayTitle = insight.time_period !== "N/A"
                ? `${insight.title} (${insight.time_period})`
                : insight.title;

              return (
                <Grid item xs={12} key={insight.id}>
                  <InsightAccordion
                    title={displayTitle}
                    text={insight.text}
                    summary={insight.summary}
                    defaultExpanded={true}
                    isLoading={insight.isThinking}
                    isDone={insight.isDone}
                  />
                </Grid>
              );
            })}
          </Grid>
        </Fade>
      )}
    </Box>
  );
};

export default DashboardInsights;